import Preset from 'app/interface/data/funnel/preset'
import Presets from 'app/interface/redux/types/data/funnel/presets'

/**
 * The four presets that ship with the app. Exported because PresetActions.update
 * rebuilds `data` as built-ins followed by the user's saved templates, and it
 * needs this list to do that without duplicating it.
 *
 * Built-ins stay first: the funnel action tests index `presets.data[1]`, so the
 * ordering is already load-bearing.
 */
export const BuiltInPresets: Preset[] = [
  // Custom
  {
    id: 1,
    icon: 'fas fa-cogs',
    name: 'Custom',
    desc: 'Enter a description that best fits what types of conversions are made in this stage',
    stages: [
      {
        name: 'Stage 1',
        icon: 'fa fa-question',
        desc: 'Stage 1',
        support: ['conversions'],
        order: 1,
        groups: [],
        estimate: false,
        conversion_name: '',
        est_conversion_name: '',
        show_total: true,
        is_kpi: false,
        is_sub_kpi: false,
        show_overview: true,
        cost_per_metric: 1,
      },
    ],
  },

  // Standard
  {
    id: 2,
    icon: 'fas fa-check-square',
    name: 'Standard',
    desc: 'The basic funnel that works for most businesses',
    stages: [
      {
        name: 'Distribution',
        icon: 'fa fa-users-class',
        desc: 'Your overall performance of your Facebook ads',
        support: ['conversions'],
        order: 1,
        groups: [
          {
            name: 'Impressions',
            desc: 'Amount of times your ads appeared on the Facebook timeline',
            sources: ['impressions'],
            extrapolation_type: 'Disabled',
          },
        ],
        estimate: false,
        conversion_name: 'Impressions',
        est_conversion_name: '',
        show_total: false,
        is_kpi: false,
        is_sub_kpi: false,
        show_overview: false,
        cost_per_metric: 1000,
      },
      {
        name: 'Research',
        icon: 'fas fa-search',
        desc: 'Your overall metrics of users visiting your website',
        support: ['conversions'],
        order: 2,
        groups: [
          {
            name: 'Direct Page Views',
            desc: 'Amount of times a customer visited your website directly via the url',
            sources: ['fbpixel/PageView'],
            extrapolation_type: 'Disabled',
          },
          {
            name: 'Indirect Page Views',
            desc: 'Amount of times a customer visited a page on your website from an external source',
            sources: ['fbpixel/ViewContent'],
            extrapolation_type: 'Disabled',
          },
        ],
        estimate: false,
        conversion_name: 'Page Views',
        est_conversion_name: '',
        show_total: true,
        is_kpi: false,
        is_sub_kpi: false,
        show_overview: true,
        cost_per_metric: 1,
      },
      {
        name: 'Leads',
        icon: 'fas fa-user-plus',
        desc: 'Your overall metrics of lead acquisition',
        support: ['conversions'],
        order: 3,
        groups: [
          {
            name: 'Direct Facebook Lead',
            desc: 'A lead that signed up directly on Facebook',
            sources: ['fbleads'],
            extrapolation_type: 'Disabled',
          },
          {
            name: 'Phone Call',
            desc: 'A lead that saw your ad and then called your business',
            sources: ['offline_events/AddToCart'],
            extrapolation_type: 'Modern',
          },
        ],
        estimate: false,
        conversion_name: 'Leads',
        est_conversion_name: '',
        show_total: true,
        show_overview: true,
        is_kpi: false,
        is_sub_kpi: false,
        cost_per_metric: 1,
        stage_target: 'leads',
      },
      {
        name: 'Purchases',
        icon: 'fas fa-usd-square',
        desc: 'Your overall metrics of actual revenue coming into your business',
        support: ['conversions', 'revenue'],
        order: 4,
        groups: [
          {
            name: 'Purchases',
            desc: 'Amount of purchases from your business',
            sources: ['offline_events/Purchase'],
            extrapolation_type: 'Modern',
          },
        ],
        estimate: false,
        conversion_name: 'Purchases',
        est_conversion_name: '',
        show_total: true,
        show_overview: true,
        is_kpi: true,
        is_sub_kpi: false,
        cost_per_metric: 1,
      },
    ],
  },

  // Orangetheory Fitness
  {
    id: 3,
    icon: 'fas fa-dumbbell',
    name: 'Orangetheory Fitness',
    desc: 'Funnels suited for the OTF franchise business model',
    stages: [
      {
        name: 'Distribution',
        icon: 'fa fa-users-class',
        desc: 'Your overall performance of your Facebook ads',
        support: ['conversions'],
        order: 1,
        groups: [
          {
            name: 'Impressions',
            desc: 'Amount of times your ads appeared on the Facebook timeline',
            sources: ['impressions'],
            extrapolation_type: 'Disabled',
          },
        ],
        estimate: false,
        conversion_name: 'Impressions',
        est_conversion_name: '',
        show_total: false,
        is_kpi: false,
        is_sub_kpi: false,
        show_overview: true,
        cost_per_metric: 1000,
      },
      {
        name: 'Link Clicks',
        icon: 'fa fa-search',
        desc: 'Your overall metrics of users visiting your website',
        support: ['conversions'],
        order: 2,
        groups: [
          {
            name: 'Link Clicks',
            desc: 'Amount of times someone engages with one of your ads',
            sources: ['insights/link_click'],
            extrapolation_type: 'Disabled',
          },
        ],
        estimate: false,
        conversion_name: 'Link Clicks',
        est_conversion_name: '',
        show_total: true,
        is_kpi: false,
        is_sub_kpi: false,
        show_overview: true,
        cost_per_metric: 1,
      },
      {
        name: 'Leads',
        icon: 'fa fa-user-plus',
        desc: 'Your overall metrics of lead acquisition',
        support: ['conversions'],
        order: 3,
        groups: [
          {
            name: 'Direct Facebook Lead',
            desc: 'A lead that signed up directly on Facebook',
            sources: ['fbleads'],
            extrapolation_type: 'Disabled',
          },
          {
            name: 'Phone Call',
            desc: 'A lead that saw your ad and then called your business',
            sources: ['offline_events/Lead'],
            extrapolation_type: 'Modern',
          },
          {
            name: 'Website Leads',
            desc: 'A lead that signed up via your website.',
            sources: ['fbpixel/CompleteRegistration'],
            extrapolation_type: 'Disabled',
          },
        ],
        estimate: false,
        conversion_name: 'Leads',
        est_conversion_name: '',
        show_total: true,
        is_kpi: false,
        is_sub_kpi: false,
        campaign_goal: 'lead_generation',
        data_sources_id: 2,
        show_overview: true,
        cost_per_metric: 1,
        stage_target: 'leads',
      },
      {
        name: 'Bookings',
        icon: 'fa fa-address-book',
        desc: 'People who have booked at OTF',
        support: ['conversions'],
        order: 4,
        groups: [
          {
            name: 'Bookings',
            desc: 'Total Bookings attributed to ads',
            sources: ['insights/add_payment_info'],
            extrapolation_type: 'Disabled',
          },
        ],
        estimate: true,
        conversion_name: 'Bookings',
        est_conversion_name: 'Estimated Members',
        show_total: true,
        is_kpi: true,
        is_sub_kpi: false,
        data_sources_id: 4,
        show_overview: true,
        cost_per_metric: 1,
      },
    ],
  },

  // Leads
  {
    id: 4,
    icon: 'fas fa-user-plus',
    name: 'Leads',
    desc: 'The basic funnel that works for businesses focused on acquiring leads',
    stages: [
      {
        name: 'Distribution',
        icon: 'fa fa-users-class',
        desc: 'Your overall performance of your Facebook ads',
        support: ['conversions'],
        order: 1,
        groups: [
          {
            name: 'Impressions',
            desc: 'Amount of times your ads appeared on the Facebook timeline',
            sources: ['impressions'],
            extrapolation_type: 'Disabled',
          },
        ],
        estimate: false,
        conversion_name: 'Impressions',
        est_conversion_name: '',
        show_total: false,
        is_kpi: false,
        is_sub_kpi: false,
        show_overview: true,
        cost_per_metric: 1000,
      },
      {
        name: 'Research',
        icon: 'fa fa-search',
        desc: 'Your overall metrics of users visiting your website',
        support: ['conversions'],
        order: 2,
        groups: [
          {
            name: 'Direct Page Views',
            desc: 'Amount of times a customer visited your website directly via the url',
            sources: ['fbpixel/PageView'],
            extrapolation_type: 'Disabled',
          },
          {
            name: 'Indirect Page Views',
            desc: 'Amount of times a customer visited a page on your website from an external source',
            sources: ['fbpixel/ViewContent'],
            extrapolation_type: 'Disabled',
          },
        ],
        estimate: false,
        conversion_name: 'Page Views',
        est_conversion_name: '',
        show_total: true,
        is_kpi: false,
        is_sub_kpi: false,
        show_overview: true,
        cost_per_metric: 1,
      },
      {
        name: 'Leads',
        icon: 'fa fa-user-plus',
        desc: 'Your overall metrics of lead acquisition',
        support: ['conversions'],
        order: 3,
        groups: [
          {
            name: 'Direct Facebook Lead',
            desc: 'A lead that signed up directly on Facebook',
            sources: ['fbleads'],
            extrapolation_type: 'Disabled',
          },
          {
            name: 'Phone Call',
            desc: 'A lead that saw your ad and then called your business',
            sources: ['offline_events/AddToCart'],
            extrapolation_type: 'Modern',
          },
          {
            name: 'Website Leads',
            desc: 'A lead that signed up via your website.',
            sources: ['fbpixel/Lead'],
            extrapolation_type: 'Disabled',
          },
        ],
        estimate: true,
        conversion_name: 'Leads',
        est_conversion_name: 'New Customers',
        show_total: true,
        is_kpi: true,
        is_sub_kpi: false,
        show_overview: true,
        cost_per_metric: 1,
        stage_target: 'leads',
      },
    ],
  },
]

const Default: Presets = {
  data: BuiltInPresets,
  // `false`, not `true`: nothing ever flipped this, so `true` was already a lie —
  // and it becomes a load-bearing one now that there is a real fetch behind it.
  loading: false,
  timestamp: +new Date(),
}

export default Default
